import { useEffect, useRef, useState, type ChangeEvent } from 'react'

import { usePlayer } from '@/audio/store'
import { cn } from '@/lib/cn'
import { formatDuration } from '@/lib/format'

/** Matches the thumb size in the `.range-input` rules in styles/index.css. */
const THUMB_REM = 0.875

/**
 * The thumb centre travels from `thumb/2` to `width - thumb/2`, not from 0 to
 * 100%. Painting the fill at a plain percentage leaves it visibly ahead of the
 * thumb at one end and behind it at the other, so the fill follows the same
 * geometry the browser uses.
 */
function fillWidth(fraction: number): string {
  return `calc(${THUMB_REM / 2}rem + ${fraction} * (100% - ${THUMB_REM}rem))`
}

function clampFraction(value: number, max: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) return 0
  return Math.max(0, Math.min(1, value / max))
}

export interface RangeSliderProps {
  value: number
  max: number
  step?: number
  /** A dimmer fill drawn behind the main one, in the same units as `max`. */
  secondary?: number
  disabled?: boolean
  /** Accessible name — a bare range input announces only a number. */
  label: string
  /** Spoken value, e.g. "1:24 of 3:07". */
  valueText?: string
  /** Every input event while dragging. Use for optimistic display only. */
  onPreview?: (value: number) => void
  /** Once the interaction settles: pointer release, key press, or track click. */
  onCommit: (value: number) => void
  className?: string
}

/**
 * A native range input with a painted track.
 *
 * A native input is used rather than a div-based slider because it is keyboard
 * accessible and handles touch correctly for free. The visible track is drawn by
 * sibling elements underneath, and the input's own track pseudo-elements are
 * made transparent so the thumb still paints on top.
 */
export function RangeSlider({
  value,
  max,
  step = 1,
  secondary,
  disabled = false,
  label,
  valueText,
  onPreview,
  onCommit,
  className,
}: RangeSliderProps) {
  const [dragging, setDragging] = useState(false)
  const draggingRef = useRef(false)
  const pendingRef = useRef(value)
  const commitRef = useRef(onCommit)

  useEffect(() => {
    commitRef.current = onCommit
  })

  // The pointer is regularly released outside the input, so the end of a drag is
  // detected on the window rather than on the element.
  useEffect(() => {
    if (!dragging) return
    const end = () => {
      draggingRef.current = false
      setDragging(false)
      commitRef.current(pendingRef.current)
    }
    window.addEventListener('pointerup', end)
    window.addEventListener('pointercancel', end)
    return () => {
      window.removeEventListener('pointerup', end)
      window.removeEventListener('pointercancel', end)
    }
  }, [dragging])

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = Number(event.target.value)
    pendingRef.current = next
    onPreview?.(next)
    // Keyboard steps and clicks on the track have no drag to wait for.
    if (!draggingRef.current) onCommit(next)
  }

  const handlePointerDown = () => {
    if (disabled) return
    draggingRef.current = true
    setDragging(true)
  }

  const fraction = clampFraction(value, max)
  const secondaryFraction =
    typeof secondary === 'number' ? Math.max(clampFraction(secondary, max), fraction) : null

  return (
    <span className={cn('relative flex h-4 w-full items-center', className)}>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-surface-hover"
      />
      {secondaryFraction !== null ? (
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-border-strong',
            disabled && 'opacity-50',
          )}
          style={{ width: fillWidth(secondaryFraction) }}
        />
      ) : null}
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-accent',
          disabled && 'opacity-50',
        )}
        style={{ width: fillWidth(fraction) }}
      />

      <input
        type="range"
        min={0}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        aria-label={label}
        aria-valuetext={valueText}
        onChange={handleChange}
        onPointerDown={handlePointerDown}
        className={cn(
          'range-input relative h-4',
          '[&::-webkit-slider-runnable-track]:bg-transparent',
          '[&::-moz-range-track]:bg-transparent',
        )}
      />
    </span>
  )
}

export interface SeekBarProps {
  /** Elapsed and remaining labels either side of the track. */
  showTimes?: boolean
  className?: string
}

/**
 * Scrubbing is local state until the pointer is released, so a drag does not set
 * `currentTime` on the audio element dozens of times and stall the stream.
 */
export function SeekBar({ showTimes = true, className }: SeekBarProps) {
  const currentTime = usePlayer((state) => state.currentTime)
  const duration = usePlayer((state) => state.duration)
  const buffered = usePlayer((state) => state.buffered)
  const seek = usePlayer((state) => state.seek)

  const [scrubbing, setScrubbing] = useState<number | null>(null)

  const seekable = duration > 0
  const position = Math.max(0, Math.min(scrubbing ?? currentTime, seekable ? duration : 0))
  const remaining = seekable ? Math.max(0, duration - position) : 0

  return (
    <div className={cn('flex w-full items-center gap-2', className)}>
      {showTimes ? (
        <span className="w-12 shrink-0 text-right text-xs tabular-nums text-text-muted">
          {formatDuration(position)}
        </span>
      ) : null}

      <RangeSlider
        className="min-w-0 flex-1"
        value={position}
        max={seekable ? duration : 1}
        step={1}
        secondary={seekable ? buffered : undefined}
        disabled={!seekable}
        label="Seek"
        valueText={
          seekable
            ? `${formatDuration(position)} of ${formatDuration(duration)}`
            : 'Seeking unavailable'
        }
        onPreview={setScrubbing}
        onCommit={(value) => {
          setScrubbing(null)
          if (seekable) seek(value)
        }}
      />

      {showTimes ? (
        <span className="w-12 shrink-0 text-xs tabular-nums text-text-muted">
          {seekable ? `-${formatDuration(remaining)}` : '--:--'}
        </span>
      ) : null}
    </div>
  )
}
